<template>
  <div class="content">
    <div class="block-header">
      <h2>
        Experts
        <b-spinner
          v-if="loading"
          type="grow"
          label="Fetching Experts"
          variant="primary"
        />
      </h2>
      <div class="block-options">
        <a href="/admin/experts/create">
          <button
            type="button"
            class="btn btn-sm btn-primary"
          >Create Expert
          </button>
        </a>
      </div>
    </div>

    <expert-search-bar
      :target-companies="parsedProjectCompanies"
      :relevant-jobs="parsedProjectJobs"
      :countries="countriesComputed"
      :industries="industriesComputed"
      :pay-statuses="payStatuses"
      :total="total"
      :loading="loading"
      @setDisplayOptions="setDisplayOptions"
      @setQuery="setQuery"
      @setMainKeyword="setMainKeyword"
      @search="getUsers"
      @markString="markString"
    />

    <div class="results_container">
      <expert-result-block
        v-for="(user, i) in usersComputed"
        :key="i"
        :user="user"
        :current-route-name="currentRouteName"
        :routes="routesComputed"
        :wpmessage="wpmessageComputed"
        :hide-job-experiences="hideJobExperiencesComputed"
        :me="meComputed"
        :project="project"
        :main-kw="expertKeyword"
        :show-expert-btn="showExpertBtn"
        :list-type="`expert-list`"
        @updated="getUsers"
      />

      <div
        v-if="usersComputed.length > 0"
        class="pagination-container"
      >
        <button
          :disabled="loading || !userData.has_more"
          class="btn btn-primary load-more-btn"
          @click="loadMore"
        >
          <b-spinner
            v-if="loading"
            small
            label="Loading"
            variant="light"
          />
          <span v-else>
            {{ userData.has_more ? 'Load More' : 'No more results' }}
          </span>
        </button>

        <div
          v-if="userData.has_more === false && usersComputed.length > 0"
          class="text-muted text-center mt-3"
        >
          End of results
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Mark from "mark.js";
import mixin from "../../../../mixins/mixin";
import {mapState} from "vuex";

const IGNORE_PUNCTUATION = ":;.,-–—‒_(){}[]!'\"+=".split("");

function markStr(str) {
    const searchInstance = new Mark("div.result-block");
    searchInstance.mark(str, {
        element: "span",
        className: "searchHighlight",
        separateWordSearch: true,
        accuracy: "partially",
        ignorePunctuation: IGNORE_PUNCTUATION
    });
}

export default {
    name: "ExpertSearchList",
    mixins: [mixin],
    props: {
        usersInit: {
            type: String,
            required: true,
        },
        countriesInit: {
            type: String,
            required: true,
        },
        industriesInit: {
            type: String,
            required: true,
        },
        payStatusesInit: {
            type: String,
            required: true,
        },
        currentRouteName: {
            type: String,
            required: false,
            default() {
                return "";
            },
        },
        projectInit: {
            type: String,
            required: false,
            default() {
                return null;
            },
        },
        projectCompanies: {
            type: String,
            required: false,
            default() {
                return null
            }
        },
        projectJobs: {
            type: String,
            required: false,
            default() {
                return null
            }
        },
        showExpertBtn: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            url: "/admin/experts/api/index",
            countries: [],
            industries: [],
            payStatuses: [],
            wpmessage: "",
            extraStringRaw: "",
            hideJobExperiences: false,
            searchStr: [],
            myUser: {},
            routes: [],
            project: null,
            loading: false,
            expertKeyword: "",
            parsedProjectCompanies: [],
            parsedProjectJobs: []
        };
    },
    computed: {
        ...mapState({
            userData: state => state.expert.usersExpertsObj || {},
        }),
        usersComputed() {
            return this.userData?.data ?? [];
        },
        industriesComputed() {
            return Array.isArray(this.industries) ? this.industries : [];
        },
        countriesComputed() {
            return Array.isArray(this.countries) ? this.countries : [];
        },
        routesComputed() {
            return this.routes
        },
        wpmessageComputed() {
            return typeof this.wpmessage === "string" ? this.wpmessage : "";
        },
        extraString() {
            return this.extraStringRaw;
        },
        total() {
            return this.userData.total || 0;
        },
        hideJobExperiencesComputed() {
            return this.hideJobExperiences;
        },
        meComputed() {
            return this.myUser;
        },
    },
    watch: {
        projectCompanies() {
            this.parseProjectData();
        },
        projectJobs() {
            this.parseProjectData();
        }
    },
    mounted() {
        const users = JSON.parse(this.usersInit);
        this.$store.commit("expert/setUsersExpertsObj", users);
        this.countries = JSON.parse(this.countriesInit);
        this.industries = JSON.parse(this.industriesInit);
        this.payStatuses = JSON.parse(this.payStatusesInit);
        this.project = this.projectInit ? JSON.parse(this.projectInit) : null;
        this.getMyUser().then(u => (this.myUser = u));
        this.getPermittedRoutes().then(res => (this.routes = res));
        this.getSingleWpMessage("expert.whatsapp_preset_message1").then(res => (this.wpmessage = res));

        // Handle parsing of projectCompanies and projectJobs if they're strings
        this.parseProjectData();

        // Initial fetch of users
        this.getUsers(true);
    },
    methods: {
        markString(strArr) {
            this.searchStr = strArr;
        },
        async getUsers(resetResults = true) {
            this.loading = true;

            if (resetResults) {
                // Reset results for new search
                const users = { data: [], has_more: false, next_cursor: null };
                this.$store.commit("expert/setUsersExpertsObj", users);
                this.isFirstLoad = true;
            }

            const searchUrl = `${this.url}?get_json=true${this.extraString}`;
            const response = await axios.get(searchUrl);

            if (resetResults) {
                // Replace the data for new search
                this.$store.commit("expert/setUsersExpertsObj", response.data.users);
                this.isFirstLoad = false;
            } else {
                // Append data for load more
                const currentUsers = this.userData;
                const newData = {
                    data: [...currentUsers.data, ...response.data.users.data],
                    has_more: response.data.users.has_more,
                    next_cursor: response.data.users.next_cursor,
                    per_page: response.data.users.per_page,
                    total: response.data.users.total
                };
                this.$store.commit("expert/setUsersExpertsObj", newData);
            }

            this.loading = false;

            setTimeout(() => {
                if (this.searchStr.length > 0) {
                    this.searchStr.forEach(function(str) {
                        markStr(str);
                    });
                }
            }, 300);
        },
        async loadMore() {
            if (!this.userData.has_more || this.loading) {
                return;
            }

            this.loading = true;

            // Get the next cursor from current data
            const cursor = this.userData.next_cursor;
            const searchUrl = `${this.url}?get_json=true${this.extraString}&cursor=${cursor}`;

            try {
                const response = await axios.get(searchUrl);

                // Append new data to existing data
                const currentUsers = this.userData;
                const newData = {
                    data: [...currentUsers.data, ...response.data.users.data],
                    has_more: response.data.users.has_more,
                    next_cursor: response.data.users.next_cursor,
                    per_page: response.data.users.per_page,
                    total: response.data.users.total
                };
                this.$store.commit("expert/setUsersExpertsObj", newData);
                // Highlight search terms in new results
                setTimeout(() => {
                    if (this.searchStr.length > 0) {
                        this.searchStr.forEach(function(str) {
                            markStr(str);
                        });
                    }
                }, 300);
            } catch (error) {
                console.error('Error loading more users:', error);
            } finally {
                this.loading = false;
            }
        },
        setQuery(q) {
            this.extraStringRaw = q;
        },
        setMainKeyword(kw) {
            this.expertKeyword = kw;
        },
        setDisplayOptions(payload) {
            if (payload && payload.id === "hideJobExperiences") {
                this.hideJobExperiences = payload.status;
            }
        },
        parseProjectData() {
            try {
                // For projectCompanies
                if (typeof this.projectCompanies === 'string' && this.projectCompanies) {
                    // Make sure it's not an empty string
                    if (this.projectCompanies.trim() !== '') {
                        this.parsedProjectCompanies = JSON.parse(this.projectCompanies);
                        // console.log('Parsed project companies:', this.parsedProjectCompanies);
                    }
                } else if (Array.isArray(this.projectCompanies)) {
                    this.parsedProjectCompanies = this.projectCompanies;
                }

                // For projectJobs
                if (typeof this.projectJobs === 'string' && this.projectJobs) {
                    // Make sure it's not an empty string
                    if (this.projectJobs.trim() !== '') {
                        this.parsedProjectJobs = JSON.parse(this.projectJobs);
                        //console.log('Parsed project jobs:', this.parsedProjectJobs);
                    }
                } else if (Array.isArray(this.projectJobs)) {
                    this.parsedProjectJobs = this.projectJobs;
                }
            } catch (error) {
                console.error('Error parsing project data:', error);
            }
        },
    },
};
</script>

<style scoped>
.pagination-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
}

.load-more-btn {
    min-width: 120px;
    padding: 10px 20px;
}

.load-more-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
